import { Col, Row } from 'antd';
import { memo, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { HeroImage } from '@/shared/components/ui/atoms';
import type { AuthLayoutProps, ResponsiveSpans } from './types';
import './AuthLayout.css';

/**
 * Default responsive spans for content area
 */
const DEFAULT_CONTENT_SPAN: ResponsiveSpans = {
  xs: 24,
  sm: 24,
  md: 24,
  lg: 10,
  xl: 14,
};

/**
 * Default responsive spans for image area
 */
const DEFAULT_IMAGE_SPAN: ResponsiveSpans = {
  xs: 24,
  sm: 24,
  md: 24,
  lg: 14,
  xl: 10,
};

/**
 * AuthLayout Component
 *
 * A fully responsive authentication layout component that provides a two-column structure
 * for authentication pages. Features a responsive grid system with customizable
 * content and image/decorative sections that adapts to different screen sizes.
 *
 * Responsive Behavior (Ant Design Breakpoints):
 * - XL (≥1200px): Two-column layout with configurable spans
 * - LG (992px-1199px): Two-column layout, optimized for small desktops
 * - MD (768px-991px): Single-column stacked layout with topbar logo
 * - SM (576px-767px): Compact stacked layout with topbar logo
 * - XS (≤575px): Ultra-compact layout with topbar logo
 *
 * @example
 * ```tsx
 * // Basic usage with logo and image
 * <AuthLayout
 *   logo={<CompanyLogo />}
 *   image={{
 *     src: "/auth-bg.jpg",
 *     alt: "Login background"
 *   }}
 * />
 *
 * // With custom right side content
 * <AuthLayout
 *   logo={<Logo />}
 *   rightSideContent={<CustomWelcomeMessage />}
 * />
 *
 * // Customized responsive grid spans for all breakpoints
 * <AuthLayout
 *   contentSpan={{ xs: 24, sm: 24, md: 16, lg: 12, xl: 16 }}
 *   imageSpan={{ xs: 24, sm: 24, md: 8, lg: 12, xl: 8 }}
 *   backgroundColor="#ffffff"
 *   className="custom-auth-layout"
 * />
 *
 * // Mobile-first approach with custom breakpoints
 * <AuthLayout
 *   contentSpan={{ xs: 24, lg: 14 }}
 *   imageSpan={{ xs: 24, lg: 10 }}
 * />
 * ```
 *
 * @param props - The component props
 * @returns A memoized layout component with authentication-specific responsive structure
 */
const AuthLayout = memo(
  ({
    logo,
    image,
    rightSideContent,
    className = '',
    style,
    backgroundColor = '#FFFFFF',
    contentSpan = {},
    imageSpan = {},
    'aria-label': ariaLabel = 'Authentication layout',
  }: AuthLayoutProps) => {
    /**
     * Merged responsive spans with user overrides
     */
    const finalContentSpan = useMemo(
      () => ({ ...DEFAULT_CONTENT_SPAN, ...contentSpan }),
      [contentSpan]
    );

    const finalImageSpan = useMemo(
      () => ({ ...DEFAULT_IMAGE_SPAN, ...imageSpan }),
      [imageSpan]
    );
    /**
     * Memoized layout styles combining background color and custom styles
     * Optimizes re-renders when style props haven't changed
     */
    const layoutStyle = useMemo(
      () => ({
        backgroundColor,
        ...style,
      }),
      [backgroundColor, style]
    );

    /**
     * Determines the right side content based on priority:
     * 1. Custom rightSideContent (highest priority)
     * 2. HeroImage component with provided image props
     * 3. null (no content)
     */
    const rightSideElement = useMemo(() => {
      if (rightSideContent) {
        return rightSideContent;
      }

      if (image) {
        return <HeroImage {...image} />;
      }

      return null;
    }, [rightSideContent, image]);

    return (
      <div
        className={`auth-layout ${className}`.trim()}
        style={layoutStyle}
        role="main"
        aria-label={ariaLabel}
      >
        <div className="auth-layout__container">
          {logo && (
            <header className="auth-layout__logo" role="banner">
              {logo}
            </header>
          )}
          <Row gutter={0} className="auth-layout__row">
            <Col
              xs={finalContentSpan.xs}
              sm={finalContentSpan.sm}
              md={finalContentSpan.md}
              lg={finalContentSpan.lg}
              xl={finalContentSpan.xl}
              className="auth-layout__content"
              {...finalContentSpan}
            >
              <section
                className="auth-layout__outlet"
                aria-label="Authentication form"
              >
                <Outlet />
              </section>
            </Col>

            <Col
              xs={finalImageSpan.xs}
              sm={finalImageSpan.sm}
              md={finalImageSpan.md}
              lg={finalImageSpan.lg}
              xl={finalImageSpan.xl}
              className="auth-layout__image-section"
            >
              <aside aria-label="Decorative content">{rightSideElement}</aside>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
);

AuthLayout.displayName = 'AuthLayout';

export default AuthLayout;
